import { useQuery } from "react-query";

import axios from "axios";
import { DATA_SVC_RS_ADDRESS } from "constants/app.constants";

import { PdenSourceEnum } from "../../../models/pdenDataSourceSetting";
import { CumulativeData } from "../Forecasting";

export const FetchCumulativeDataQuery = "fetchCumulativeData";

const API_URL = `${DATA_SVC_RS_ADDRESS}/api/v1/data/from-well-list`;

type FetchParams = { uwi: string; pdenSource: PdenSourceEnum };

const fetchCumulativeData = async ({
  uwi,
  pdenSource
}: FetchParams): Promise<CumulativeData | null> => {
  if (uwi.length === 0) {
    return null;
  }
  const response = await axios.post(API_URL, {
    uwis: [uwi],
    fields: [
      "Performance.Oil.CumToDate",
      "Performance.Gas.CumToDate",
      "Performance.Water.CumToDate",
      "Dates.LastProdDate",
      "McD_Header.McDPrimaryProduct",
      "Performance.SteamInj.CumToDate"
    ],
    pdenSource
  });

  const rawData = response.data[uwi];
  const oilAndCondensate = parseFloat(rawData[0]) || 0;
  return {
    oil: oilAndCondensate,
    gas: parseFloat(rawData[1]) || 0,
    water: parseFloat(rawData[2]) || 0,
    condensate: oilAndCondensate,
    lastProductionDate: rawData[3],
    primaryProduct: rawData[4] === "WTR" ? "WATER" : rawData[4],
    steam: parseFloat(rawData[5]) || 0
  } as CumulativeData;
};

/**
 * Custom hook to fetch cumulative production data for oil, gas, and water using React Query.
 *
 * This hook fetches the historical performance data for the given well, which is then used to calculate the true EUR.
 *
 * @param {string} uwi - The unique well identifier for which to fetch cumulative data.
 * @returns {QueryObserverResult<CumulativeData | null>} - The result of the query, which includes the cumulative well data or null.
 */
const useFetchCumulativeData = ({ uwi, pdenSource }: FetchParams) => {
  return useQuery<CumulativeData | null>(
    [FetchCumulativeDataQuery, uwi, pdenSource],
    () => fetchCumulativeData({ uwi, pdenSource }),
    {
      enabled: uwi.length > 0,
      refetchOnWindowFocus: false
    }
  );
};

export default useFetchCumulativeData;
